import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Phone, Mail, User, Plus, Edit, Trash2, Loader2, FileDown } from "lucide-react";

export function CustomerContactsTab({
  contacts,
  contactDialogOpen,
  setContactDialogOpen,
  editContactIndex,
  contactForm,
  setContactForm,
  onOpenDialog,
  onSave,
  onDelete,
  saving,
  onExportAll,
  onExportContact,
  canExport = true,
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Ansprechpartner</CardTitle>
          <CardDescription>Kontaktpersonen bei diesem Kunden</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {canExport && contacts.length > 0 && (
            <Button size="sm" variant="outline" onClick={onExportAll} data-testid="export-all-contacts-placetel-btn">
              <FileDown className="w-4 h-4 mr-2" />
              Placetel-CSV
            </Button>
          )}
          <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => onOpenDialog(null, null)} data-testid="add-contact-btn">
                <Plus className="w-4 h-4 mr-2" />
                Hinzufügen
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editContactIndex !== null ? "Kontakt bearbeiten" : "Kontakt hinzufügen"}</DialogTitle>
              <DialogDescription>Kontaktdaten des Ansprechpartners erfassen</DialogDescription>
            </DialogHeader>
            <form onSubmit={onSave}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-salutation">Anrede</Label>
                    <Select
                      value={contactForm.salutation}
                      onValueChange={(value) => setContactForm({ ...contactForm, salutation: value })}
                    >
                      <SelectTrigger id="contact-salutation" data-testid="contact-salutation-select">
                        <SelectValue placeholder="Anrede..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Herr">Herr</SelectItem>
                        <SelectItem value="Frau">Frau</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-title">Titel</Label>
                    <Select
                      value={contactForm.title}
                      onValueChange={(value) => setContactForm({ ...contactForm, title: value })}
                    >
                      <SelectTrigger id="contact-title" data-testid="contact-title-select">
                        <SelectValue placeholder="Titel..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dr.">Dr.</SelectItem>
                        <SelectItem value="Dr. med.">Dr. med.</SelectItem>
                        <SelectItem value="Prof.">Prof.</SelectItem>
                        <SelectItem value="Prof. Dr.">Prof. Dr.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-first-name">Vorname</Label>
                    <Input
                      id="contact-first-name"
                      value={contactForm.first_name}
                      onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                      data-testid="contact-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Nachname *</Label>
                    <Input
                      id="contact-name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-position">Position</Label>
                  <Input
                    id="contact-position"
                    value={contactForm.position}
                    onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                    data-testid="contact-position-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone">Telefon</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">E-Mail</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saving} data-testid="contact-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {contacts.length > 0 ? (
          <div className="space-y-3">
            {contacts.map((contact, index) => (
              <div key={contact.id || `contact-${index}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium break-words">
                      {[contact.salutation, contact.title, contact.first_name, contact.name].filter(Boolean).join(' ')}
                    </p>
                    {contact.position && <p className="text-sm text-muted-foreground break-words">{contact.position}</p>}
                    <div className="flex flex-col sm:flex-row sm:gap-3 mt-1 text-sm text-muted-foreground">
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                          <Phone className="w-3 h-3 shrink-0" />
                          {contact.phone}
                        </a>
                      )}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary transition-colors break-all">
                          <Mail className="w-3 h-3 shrink-0" />
                          {contact.email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 self-end sm:self-auto shrink-0">
                  {canExport && (
                    <Button variant="ghost" size="icon" onClick={() => onExportContact(index)} data-testid={`export-contact-placetel-${index}`} title="Als CSV exportieren (Placetel)">
                      <FileDown className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => onOpenDialog(contact, index)} data-testid={`edit-contact-${index}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => onDelete(index)} data-testid={`delete-contact-${index}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state py-8">
            <User className="w-12 h-12 text-muted-foreground/30" />
            <p className="text-muted-foreground mt-4">Keine Ansprechpartner hinterlegt</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
